import { Plural, t, Trans } from '@lingui/macro';
import { useDebounce } from '@luminovo/commons';
import {
    colorSystem,
    Dialog,
    DialogContent,
    FieldText,
    InteractiveCard,
    PrimaryButton,
    Text,
} from '@luminovo/design-system';
import { PartLite, PartLiteTypes } from '@luminovo/http-client';
import { Box, List, Skeleton } from '@mui/material';
import React from 'react';
import { usePartSearch } from '../hooks/usePartSearch';
import { PartSearchDialogProps } from '../types';
import { PartListItem } from './PartListItem';

function partLiteHasMpn(part: PartLite): part is PartLite & {
    kind: PartLiteTypes.OffTheShelf | PartLiteTypes.Unknown | PartLiteTypes.RawSpecification;
    mpn: string | null | undefined;
} {
    return (
        part.kind === PartLiteTypes.OffTheShelf ||
        part.kind === PartLiteTypes.Unknown ||
        part.kind === PartLiteTypes.RawSpecification
    );
}
export function PartSearchDialog({
    open,
    onClose,
    value,
    suggestedParts = [],
    onChange,
    title = t`Select offered part`,
}: PartSearchDialogProps): JSX.Element {
    const [selectedPart, setSelectedPart] = React.useState(value);
    const [search, setSearch] = React.useState(() => {
        if (value && partLiteHasMpn(value)) {
            return value.mpn ?? '';
        }
        return '';
    });
    const debouncedSearch = useDebounce(search, 500);

    const { partOptions, isLoading } = usePartSearch({
        search: debouncedSearch,
        suggestedParts,
    });

    return (
        <Dialog title={title} open={open} onClose={onClose}>
            <DialogContent
                sx={{
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FieldText
                        placeholder={t`Search for a part`}
                        type="search"
                        fullWidth
                        autoFocus
                        value={search}
                        onChange={(value) => setSearch(value ?? '')}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                    />
                    <Box
                        sx={{
                            minHeight: '0px',
                            overflow: 'auto',
                            maxHeight: '600px',
                            gap: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {isLoading ? (
                            <InteractiveCard selectable={false} sx={{ padding: 1 }}>
                                <Skeleton variant="text" />
                            </InteractiveCard>
                        ) : (
                            <Text color={colorSystem.neutral[6]}>
                                <Plural
                                    value={partOptions.length}
                                    zero={`No results found`}
                                    one={`Showing one result`}
                                    other={`Showing ${partOptions.length} results`}
                                />
                            </Text>
                        )}
                        {!isLoading && (
                            <List disablePadding sx={{ overflow: 'auto' }}>
                                {partOptions.map(({ part, id, image, label, description, isSuggested }) => (
                                    <PartListItem
                                        key={id}
                                        query={debouncedSearch}
                                        selected={id === selectedPart?.id}
                                        onClick={() => setSelectedPart(part)}
                                        primaryText={label}
                                        secondaryText={description}
                                        image={image ?? ''}
                                        isSuggested={isSuggested}
                                    />
                                ))}
                            </List>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 3 }}>
                <PrimaryButton onClick={() => onChange(selectedPart)}>
                    <Trans>Select</Trans>
                </PrimaryButton>
            </Box>
        </Dialog>
    );
}
